import { forwardRef } from 'react'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * A document's processing state as icon plus coloured word.
 *
 * The icon is not decoration: red and green differ mostly by hue, so the
 * design system pairs every status with its own glyph and its own word. That
 * is also why `iconOnly` still renders the word for screen readers — a bare
 * coloured glyph names nothing.
 *
 * The words are Figma's and are fixed; the Flutter app's longer labels
 * ("zu prüfen", "fertig analysiert") are not used here.
 */

export type FxStatus = 'neu' | 'inProgress' | 'unclear' | 'done'

const STATUS: Record<
  FxStatus,
  { label: string; icon: string; tone: string; iconTone?: string; chip: string }
> = {
  neu: {
    label: 'noch nicht verarbeitet',
    icon: 'refresh',
    // "Not yet processed" is the one state that should not compete for
    // attention, so the *glyph* stays faint — but the word carries the
    // meaning and `--fx-text-disabled` is 1.57:1 on white, which the token
    // file itself calls "not a colour for reading text". In the document
    // table this word is the whole content of the status cell, so it takes
    // `text-muted` (5.7:1) and the quiet treatment lives on the icon.
    tone: 'text-text-muted',
    iconTone: 'text-text-disabled',
    chip: 'bg-fill text-text-muted',
  },
  inProgress: {
    label: 'in Verarbeitung',
    icon: 'schedule',
    tone: 'text-warning-text',
    chip: 'bg-warning-subtle text-warning-text',
  },
  unclear: {
    label: 'unklar',
    icon: 'error',
    tone: 'text-danger-text',
    chip: 'bg-danger-subtle text-danger-text',
  },
  done: {
    label: 'analysiert',
    icon: 'check_circle',
    tone: 'text-success-text',
    chip: 'bg-success-subtle text-success-on-subtle',
  },
}

export interface FxStatusLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: FxStatus
  /** Show only the glyph; the word stays in the accessibility tree. */
  iconOnly?: boolean
  /**
   * A person has confirmed the unclear fields. Beyond the design system's
   * four states, but part of its fixed vocabulary, and the artboards show it
   * on a reviewed document. Renders `done`'s glyph with the word "geprüft".
   */
  reviewed?: boolean
  /** The tonal badge the artboards use for a confirmed result. */
  chip?: boolean
  /** 14px instead of 16px, for table rows and card overlays. */
  size?: 'small' | 'default'
}

export const FxStatusLabel = forwardRef<HTMLSpanElement, FxStatusLabelProps>(
  ({ status, iconOnly = false, reviewed = false, chip = false, size = 'default', className, ...props }, ref) => {
    const s = STATUS[status] ?? STATUS.neu
    const label = reviewed ? 'geprüft' : s.label
    const small = size === 'small'
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-2 whitespace-nowrap',
          small ? 'text-small' : 'text-body',
          chip ? cn('rounded-sm px-2 py-[2px] font-semibold', s.chip) : s.tone,
          className,
        )}
        {...props}
      >
        <FxIcon name={s.icon} size={small ? 18 : 20} className={chip ? undefined : s.iconTone} />
        <span className={cn(iconOnly && 'sr-only')}>{label}</span>
      </span>
    )
  },
)
FxStatusLabel.displayName = 'FxStatusLabel'
